<template>
    <span>
        <v-btn outlined color="primary" class="mr-2" @click="open">
            <v-icon left small>mdi-newspaper-variant-outline</v-icon>
            Novos no DJEN
            <span v-if="count" class="djen-count">{{ count }}</span>
        </v-btn>

        <v-dialog v-model="show" max-width="1100" scrollable>
            <v-card>
                <v-card-title class="djen-title">
                    <div>
                        <div>Novos no DJEN</div>
                        <div class="djen-subtitle">
                            Habilitações, impugnações e classificações de crédito em que a AJ foi intimada no Diário de Justiça Eletrônico Nacional e que ainda não estão cadastradas.
                            <span v-if="lastSyncAt">Última busca: {{ formatDateTime(lastSyncAt) }}.</span>
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn outlined small color="primary" class="mr-2" :loading="syncing || running" @click="syncNow">
                        <v-icon left small>mdi-cloud-sync-outline</v-icon>Buscar agora
                    </v-btn>
                    <v-btn icon @click="show = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>

                <div class="djen-toolbar">
                    <v-btn-toggle v-model="status" mandatory dense color="primary" @change="load">
                        <v-btn value="new" small>Novos <span class="djen-toggle-count">{{ counts.new }}</span></v-btn>
                        <v-btn value="ignored" small>Ignorados <span class="djen-toggle-count">{{ counts.ignored }}</span></v-btn>
                    </v-btn-toggle>
                    <v-autocomplete v-model="clientId" :items="clients" item-value="id" item-text="name" placeholder="Todos os casos" dense outlined hide-details clearable
                        class="djen-field" @change="load"></v-autocomplete>
                    <v-text-field v-model="term" placeholder="Buscar por número, parte ou classe" prepend-inner-icon="mdi-magnify" dense outlined hide-details clearable
                        class="djen-field" @keyup.enter="load" @click:clear="term = ''; load()"></v-text-field>
                </div>

                <v-card-text class="djen-body">
                    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3"></v-progress-linear>
                    <ui-empty v-if="!loading && !items.length" icon="mdi-newspaper-check" :text="status === 'new' ? 'Nenhum incidente novo no DJEN.' : 'Nenhum incidente ignorado.'" />

                    <article v-for="item in items" :key="item.id" class="djen-item">
                        <div class="djen-item__head">
                            <div class="djen-item__main">
                                <div class="djen-item__number">
                                    {{ item.process_number_masked }}
                                    <ui-badge :text="item.class_name" tone="primary" :dot="false" class="ml-2" />
                                </div>
                                <div class="djen-muted">
                                    {{ formatDate(item.available_on) }} · {{ item.communication_type }} · {{ [item.tribunal, item.organ].filter(Boolean).join(' · ') }}
                                    <span v-if="item.publications_count > 1"> · {{ item.publications_count }} publicações</span>
                                </div>
                            </div>
                            <div class="djen-item__case">
                                <span v-if="item.client_name" class="djen-case"><v-icon x-small color="#16a34a">mdi-briefcase-check-outline</v-icon> {{ item.client_name }}</span>
                                <span v-else class="djen-case djen-case--missing"><v-icon x-small color="#b54708">mdi-briefcase-alert-outline</v-icon> Caso não identificado</span>
                            </div>
                        </div>

                        <div class="djen-people">
                            <div>
                                <div class="djen-people__label">Destinatários</div>
                                <div v-for="(party, index) in mainParties(item)" :key="'p' + index" class="djen-people__row">{{ party.name }} <span class="djen-people__muted">{{ poleName(party.pole) }}</span></div>
                                <div v-if="item.parties.length > mainParties(item).length" class="djen-people__muted">+{{ item.parties.length - mainParties(item).length }} intimados</div>
                            </div>
                            <div v-if="item.lawyers.length">
                                <div class="djen-people__label">Advogados intimados</div>
                                <div v-for="(lawyer, index) in item.lawyers" :key="'l' + index" class="djen-people__row">
                                    {{ lawyer.name }} <span v-if="lawyer.oab" class="djen-people__muted">OAB {{ lawyer.state }} {{ lawyer.oab }}</span>
                                    <span v-if="lawyer.procurator" class="djen-chip djen-chip--ok" :title="lawyer.procurator.name"><v-icon x-small color="#15803d">mdi-account-check-outline</v-icon> Procurador cadastrado · {{ lawyer.procurator.creditors }} credor(es)</span>
                                    <span v-else class="djen-chip">Não cadastrado</span>
                                </div>
                            </div>
                        </div>

                        <div class="djen-text" :class="{ 'djen-text--open': expanded === item.id }">{{ item.text }}</div>
                        <div class="djen-item__actions">
                            <v-btn text x-small color="primary" @click="expanded = expanded === item.id ? null : item.id">
                                {{ expanded === item.id ? 'Recolher texto' : 'Ver texto completo' }}
                            </v-btn>
                            <v-btn v-if="item.link" text x-small color="primary" :href="item.link" target="_blank" rel="noopener">
                                <v-icon left x-small>mdi-open-in-new</v-icon>Abrir no tribunal
                            </v-btn>
                            <v-spacer></v-spacer>
                            <template v-if="status === 'new'">
                                <v-menu offset-y>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn small depressed color="primary" v-bind="attrs" v-on="on">
                                            <v-icon left small>mdi-plus</v-icon>Cadastrar<v-icon right small>mdi-menu-down</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item @click="openRegister(item, 'proceeding')"><v-list-item-title>Como impugnação / incidente</v-list-item-title></v-list-item>
                                        <v-list-item @click="openRegister(item, 'other_proceeding')"><v-list-item-title>Em Outros processos</v-list-item-title></v-list-item>
                                    </v-list>
                                </v-menu>
                                <v-btn small outlined color="primary" class="ml-2" @click="openLink(item)"><v-icon left small>mdi-link-variant</v-icon>Vincular</v-btn>
                                <v-btn small text color="grey darken-1" class="ml-1" @click="openIgnore(item)">Ignorar</v-btn>
                            </template>
                            <template v-else>
                                <span class="djen-muted mr-3">Ignorado: {{ item.ignored_reason }}</span>
                                <v-btn small outlined color="primary" :loading="busy === item.id" @click="unignore(item)">Voltar a exibir</v-btn>
                            </template>
                        </div>
                    </article>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Cadastrar / vincular / ignorar -->
        <v-dialog v-model="action.show" max-width="560" persistent>
            <v-card v-if="action.item">
                <v-card-title>{{ actionTitle }}</v-card-title>
                <v-card-text>
                    <p class="djen-muted">{{ action.item.process_number_masked }} · {{ action.item.class_name }}</p>
                    <template v-if="action.type === 'register'">
                        <v-autocomplete v-model="action.client_id" :items="clients" item-value="id" item-text="name" label="Caso *" outlined dense hide-details="auto" class="mb-3"></v-autocomplete>
                        <v-select v-if="action.module === 'proceeding'" v-model="action.proceeding_type" :items="proceedingTypes" item-value="id" item-text="name" label="Tipo *" outlined dense hide-details="auto"></v-select>
                        <template v-else>
                            <v-text-field v-model="action.subject" label="Assunto / objeto *" outlined dense hide-details="auto" class="mb-3"></v-text-field>
                            <v-select v-model="action.process_type" :items="otherProcessTypes" item-value="id" item-text="name" label="Tipo de processo *" outlined dense hide-details="auto"></v-select>
                        </template>
                        <p class="djen-muted mt-3 mb-0">O número, a vara e as partes (autor, réu, recuperanda e AJ) vêm preenchidos da publicação.</p>
                    </template>
                    <template v-else-if="action.type === 'link'">
                        <v-select v-model="action.module" :items="modules" item-value="id" item-text="name" label="Módulo" outlined dense hide-details="auto" class="mb-3" @change="loadTargets"></v-select>
                        <v-autocomplete v-model="action.client_id" :items="clients" item-value="id" item-text="name" label="Caso" outlined dense hide-details="auto" class="mb-3" @change="loadTargets"></v-autocomplete>
                        <v-autocomplete v-model="action.target_id" :items="targets" item-value="id" item-text="label" label="Processo cadastrado *" :loading="action.loadingTargets"
                            no-data-text="Nenhum processo cadastrado neste caso" outlined dense hide-details="auto"></v-autocomplete>
                        <p class="djen-muted mt-3 mb-0">As próximas publicações deste número passam a aparecer no processo escolhido.</p>
                    </template>
                    <v-textarea v-else v-model="action.reason" label="Motivo *" rows="3" auto-grow outlined hide-details="auto"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text :disabled="action.saving" @click="action.show = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="action.saving" :disabled="!actionValid" @click="confirmAction">Confirmar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </span>
</template>

<script>
import axios from 'axios';
import { displayDate } from '@/common/util/date-time';
import proceedingTypes from "@/common/data/proceeding-types.data";
import { processTypes as otherProcessTypes } from "@/common/data/other-proceeding.data";

// "Novos no DJEN": incidentes de credito capturados no DJEN e ainda nao
// cadastrados. `base` define a area de permissao (Impugnacoes ou Outros processos).
export default {
    name: "DjenNewButton",

    props: {
        base: { type: String, default: "/api/admin/proceedings" },
    },

    data: () => ({
        proceedingTypes,
        otherProcessTypes,
        modules: [{ id: 'proceeding', name: 'Impugnações' }, { id: 'other_proceeding', name: 'Outros processos' }],
        count: 0,
        show: false,
        loading: false,
        syncing: false,
        running: false,
        status: 'new',
        clientId: null,
        term: '',
        items: [],
        counts: { new: 0, ignored: 0 },
        lastSyncAt: null,
        clients: [],
        expanded: null,
        busy: null,
        targets: [],
        action: { show: false, type: null, item: null },
    }),

    computed: {
        actionTitle() {
            if (this.action.type === 'register') return this.action.module === 'proceeding' ? 'Cadastrar como impugnação' : 'Cadastrar em Outros processos';
            return this.action.type === 'link' ? 'Vincular a processo cadastrado' : 'Ignorar incidente';
        },
        actionValid() {
            const a = this.action;
            if (a.type === 'register') return !!a.client_id && (a.module === 'proceeding' ? !!a.proceeding_type : !!(a.subject && a.process_type));
            if (a.type === 'link') return !!(a.module && a.target_id);
            return !!(a.reason && a.reason.trim());
        },
    },

    created() {
        this.loadCount();
    },

    methods: {
        formatDate(value) {
            return value ? this.$moment(String(value).slice(0, 10)).format('DD/MM/YYYY') : '';
        },

        formatDateTime(value) {
            return value ? displayDate(value).format('DD/MM/YYYY HH:mm') : '';
        },

        poleName(pole) {
            return ({ A: 'Polo ativo', P: 'Polo passivo', T: 'Terceiro' })[pole] || '';
        },

        // Autor e reu; os intimados em massa (terceiros) so entram no "+N".
        mainParties(item) {
            const main = (item.parties || []).filter((party) => party.pole === 'A' || party.pole === 'P');
            return (main.length ? main : item.parties || []).slice(0, 6);
        },

        async loadCount() {
            try {
                const result = await axios.get(`${this.base}/djen/new/count`);
                this.count = result.data.content.total;
            } catch (error) {
                this.count = 0;
            }
        },

        async open() {
            this.show = true;
            if (!this.clients.length) this.loadClients();
            await this.load();
        },

        async loadClients() {
            try {
                const result = await axios.get('/api/clients', { params: { limit: 100000 } });
                this.clients = (result.data.content && result.data.content.data) || [];
            } catch (error) {
                this.clients = [];
            }
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`${this.base}/djen/new`, { params: { status: this.status, client_id: this.clientId || undefined, term: this.term || undefined } });
                const content = result.data.content;
                this.items = content.data;
                this.counts = content.counts;
                this.lastSyncAt = content.last_sync_at;
                this.running = content.running;
                this.count = content.counts.new;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        async syncNow() {
            this.syncing = true;
            try {
                await axios.post(`${this.base}/djen/sync`);
                this.$dialog.notify.info('Busca no DJEN iniciada. A API do CNJ limita as consultas; atualize em alguns minutos.', { position: 'top-right', timeout: 5000 });
                this.running = true;
                setTimeout(() => { if (this.show) this.load(); }, 20000);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.syncing = false;
            }
        },

        // Tipo sugerido pela classe processual da publicacao.
        suggestedType(item) {
            const name = String(item.class_name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
            if (name.includes('HABILITA')) return 1;
            if (name.includes('CLASSIFICA')) return 4;
            if (name.includes('RESTITUI')) return 5;
            if (name.includes('IMPUGNA')) return 3;
            return null;
        },

        openRegister(item, module) {
            this.action = {
                show: true, type: 'register', item, module, saving: false,
                client_id: item.client_id || null,
                proceeding_type: this.suggestedType(item),
                subject: item.class_name ? item.class_name.charAt(0) + item.class_name.slice(1).toLowerCase() : '',
                process_type: /TRABALH/i.test(item.class_name || '') ? 1 : 2,
            };
        },

        openLink(item) {
            this.targets = [];
            this.action = { show: true, type: 'link', item, module: 'proceeding', client_id: item.client_id || null, target_id: null, saving: false, loadingTargets: false };
            this.loadTargets();
        },

        openIgnore(item) {
            this.action = { show: true, type: 'ignore', item, reason: '', saving: false };
        },

        async loadTargets() {
            this.action.target_id = null;
            this.targets = [];
            if (!this.action.client_id) return;
            this.$set(this.action, 'loadingTargets', true);
            try {
                const isProceeding = this.action.module === 'proceeding';
                const result = await axios.get(isProceeding ? '/api/admin/proceedings' : '/api/admin/other-proceedings', {
                    params: { page: 1, limit: 500, search: JSON.stringify({ client_id: this.action.client_id }) },
                });
                const rows = (result.data.content && result.data.content.data) || [];
                this.targets = rows.map((row) => ({
                    id: row.id,
                    label: isProceeding
                        ? `${row.process_number} · ${(proceedingTypes.find((type) => type.id === Number(row.type)) || {}).name || ''}`
                        : `${row.process_number || 'Sem número'} · ${row.subject || ''}`,
                }));
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.action.loadingTargets = false;
            }
        },

        async confirmAction() {
            const a = this.action;
            a.saving = true;
            try {
                if (a.type === 'register') {
                    const result = await axios.post(`${this.base}/djen/${a.item.id}/register`, {
                        module: a.module, client_id: a.client_id, type: a.proceeding_type, subject: a.subject, process_type: a.process_type,
                    });
                    const created = result.data.content;
                    this.$dialog.notify.success('Processo cadastrado a partir do DJEN.', { position: 'top-right', timeout: 3000 });
                    this.action.show = false;
                    this.show = false;
                    this.$emit('changed');
                    this.$router.push(created.module === 'proceeding' ? `/impugnacoes/${created.id}` : `/outros-processos/${created.id}`);
                    return;
                }
                if (a.type === 'link') {
                    await axios.post(`${this.base}/djen/${a.item.id}/link`, { module: a.module, target_id: a.target_id });
                    this.$dialog.notify.success('Publicação vinculada ao processo.', { position: 'top-right', timeout: 3000 });
                } else {
                    await axios.post(`${this.base}/djen/${a.item.id}/ignore`, { reason: a.reason });
                    this.$dialog.notify.success('Incidente ignorado.', { position: 'top-right', timeout: 3000 });
                }
                this.action.show = false;
                this.$emit('changed');
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 });
            } finally {
                a.saving = false;
            }
        },

        async unignore(item) {
            this.busy = item.id;
            try {
                await axios.post(`${this.base}/djen/${item.id}/unignore`);
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.busy = null;
            }
        },
    },
};
</script>

<style scoped>
.djen-count { margin-left: 8px; min-width: 20px; padding: 0 6px; border-radius: 999px; background: #dc2626; color: #fff; font-size: 11px; font-weight: 700; line-height: 18px; }
.djen-title { align-items: flex-start; gap: 12px; flex-wrap: nowrap; }
.djen-subtitle { font-size: 13px; font-weight: 400; color: #667085; line-height: 1.45; margin-top: 4px; max-width: 760px; word-break: normal; }
.djen-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 0 24px 12px; }
.djen-toggle-count { margin-left: 6px; font-weight: 700; }
.djen-field { max-width: 300px; }
.djen-body { max-height: 68vh; }
.djen-muted { font-size: 12.5px; color: #667085; }
.djen-item { padding: 14px 16px; border: 1px solid #e4e7ec; border-radius: 10px; margin-bottom: 12px; background: #fff; }
.djen-item__head { display: flex; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.djen-item__main { flex: 1 1 420px; min-width: 0; }
.djen-item__number { display: flex; align-items: center; flex-wrap: wrap; font-size: 15px; font-weight: 700; color: #101828; }
.djen-case { font-size: 12.5px; font-weight: 600; color: #166534; }
.djen-case--missing { color: #b54708; }
.djen-people { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 6px 24px; margin-top: 10px; font-size: 13px; color: #344054; }
.djen-people__label { font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #667085; margin-bottom: 2px; }
.djen-people__row { line-height: 1.7; }
.djen-people__muted { font-size: 12px; color: #667085; }
.djen-chip { display: inline-flex; align-items: center; gap: 3px; margin-left: 6px; padding: 0 8px; border-radius: 999px; background: #f2f4f7; color: #475467; font-size: 11px; font-weight: 600; line-height: 18px; }
.djen-chip--ok { background: #dcfce7; color: #15803d; }
.djen-text { margin-top: 10px; padding: 10px 12px; border-radius: 8px; background: #f9fafb; font-size: 12.5px; color: #344054; white-space: pre-wrap; max-height: 64px; overflow: hidden; }
.djen-text--open { max-height: none; }
.djen-item__actions { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
</style>
